/* eslint-disable no-unneeded-ternary */
import MuxPlayer from "@mux/mux-player-react";

import React, { useState, useEffect, useRef } from "react";

import { observer } from "mobx-react";
import * as PropTypes from "prop-types";
import styled from "styled-components";

import Icon from "../../Icon/Icon";
import VideoOverlay from "./VideoOverlay";

const StyledPlayerWrapper = styled.div`
  position: relative;
  width: 100%;

  /* Conditional styling based on whether minHeight is set */
  ${({ $minHeight, $mobileMinHeight }) => {
    // If minHeight is set, use fixed height mode (traditional)
    if ($minHeight || $mobileMinHeight) {
      return `
        height: 100%;
        min-height: ${$minHeight || "450px"};
        overflow: hidden;

        @media (max-width: 768px) {
          min-height: ${$mobileMinHeight || "250px"};
        }

        mux-player {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          --controls: none;
          --media-object-fit: cover;
          --media-object-position: center;
        }

        mux-player video {
          object-fit: cover;
          object-position: center;
        }
      `;
    }

    // Otherwise, use dynamic height mode (let video control)
    return `
      @media (max-width: 768px) {
        min-height: 250px;
      }

      mux-player {
        display: block;
        width: 100%;
        height: auto;
        aspect-ratio: auto; /* Let video determine its own aspect ratio */
        --controls: none;
      }

      mux-player video {
        width: 100%;
        height: auto;
        display: block;
      }
    `;
  }}

  /* Ensure poster image fills properly in both modes */
  mux-player::part(poster),
  mux-player img[slot="poster"],
  mux-player [part~="poster"] {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  /* Position overlays and content absolutely over the video */
  .video-overlay,
  .hero-content-wrapper,
  > div:not(:first-child) {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
  }

  .video-overlay *,
  .hero-content-wrapper * {
    pointer-events: auto;
  }
`;

const StyledResponsiveWrapper = styled.div`
  height: 100%;
  width: 100%;
  position: relative;

  mux-player {
    width: 100%;
    height: 100%;
  }
`;

const StyledVolumeWrapper = styled.div`
  position: absolute;
  z-index: 1;
  cursor: pointer;
  bottom: 1rem;
  right: 1rem;
`;

const StyledContentWrapper = styled.div`
  color: ${({ theme }) => theme.negativeTextColor};
`;

function MuxVideo(props) {
  const {
    children,
    playbackId,
    overlayColor = "transparent",
    loop = false,
    muted = false,
    isBackgroundVideo = true,
    isPaused = true,
    headingText,
    setIsPaused,
    withOverlay,
    height,
    minHeight,
    mobileMinHeight,
    isPauseable = true,
    videoHeroType,
    overlayBackground,
    volumeToggle = false,
    posterUrl,
  } = props;

  const VideoWrapperComponent = isBackgroundVideo
    ? StyledPlayerWrapper
    : StyledResponsiveWrapper;

  // Create a ref to attach to the MuxPlayer element
  const playerRef = useRef(null);

  // Single source of truth for mute state
  const [isMuted, setIsMuted] = useState(
    isBackgroundVideo || muted || !isPaused
  );

  // Determine autoPlay value based on video type
  const getAutoPlayValue = () => {
    if (isBackgroundVideo) return "muted";
    if (!isPaused) return "muted";
    return false;
  };

  // For looping videos, browser cache prevents re-downloading on each loop
  // Using "auto" ensures smooth autoplay, and the browser's HTTP cache
  // will serve subsequent loops from cache (Mux CDN sends proper cache headers)
  const getPreloadValue = () => {
    // For autoplay videos, preload auto ensures smooth playback start
    if (isBackgroundVideo || !isPaused) return "auto";
    // For non-autoplay looping videos, metadata is sufficient
    if (loop) return "metadata";
    return "auto";
  };

  // For looping videos, use a single rendition to improve browser caching
  // Per Mux docs: "Configuring your player to use a single rendition instead of
  // multiple ones can make it easier for a browser to cache video"
  // Shorter videos (<60s) are more likely to stay in browser buffers
  const getPlayerProps = () => {
    const baseProps = {
      playbackId,
      streamType: "on-demand",
      autoPlay: getAutoPlayValue(),
      loop,
      muted: isMuted,
      paused: isBackgroundVideo ? false : isPaused,
      poster: posterUrl,
      playsInline: true,
      preload: getPreloadValue(),
    };

    // For looping videos, prefer native playback and lock to a single quality
    // This helps browsers cache the video better and prevents quality switching
    // which can clear buffers and cause re-downloading
    if (loop) {
      // Use preferPlayback="native" to leverage browser's native video caching
      // This is especially effective for short looping videos
      return {
        ...baseProps,
        preferPlayback: "native",
      };
    }

    return baseProps;
  };

  // IntersectionObserver: Pause video when out of viewport to save bandwidth
  // This prevents unnecessary looping/downloading when the video isn't visible
  // Per Mux best practices: pause background videos when not in view
  useEffect(() => {
    // Only run this logic for background videos that are meant to loop
    if (!isBackgroundVideo || !playerRef.current || !loop) {
      return;
    }

    const intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const muxPlayerElement = entry.target;

          // Check if the video is *fully* out of the viewport
          if (!entry.isIntersecting || entry.intersectionRatio < 0.99) {
            // Pause the player when out of view
            muxPlayerElement.pause();
          } else {
            // Play the player when back in view
            muxPlayerElement.play();
          }
        });
      },
      {
        threshold: 0.99, // Trigger when 99% of the element is visible
      }
    );

    intersectionObserver.observe(playerRef.current);

    // Cleanup function - disconnect observer when component unmounts
    // eslint-disable-next-line consistent-return
    return () => {
      if (playerRef.current) {
        intersectionObserver.unobserve(playerRef.current);
      }
      intersectionObserver.disconnect();
    };
  }, [isBackgroundVideo, loop]); // Re-run only if these props change

  return (
    <VideoWrapperComponent
      $height={height}
      $minHeight={minHeight}
      $mobileMinHeight={mobileMinHeight}
      data-testid="mux-video-component"
      isPauseable={isPauseable}
      videoHeroType={videoHeroType}
      className="mux-video-component-wrapper"
    >
      {playbackId && (
        <>
          <MuxPlayer ref={playerRef} {...getPlayerProps()} />
          {volumeToggle && (
            <StyledVolumeWrapper>
              {isMuted ? (
                <Icon
                  icon="speakerOff"
                  onClick={() => setIsMuted(false)}
                  size={28}
                />
              ) : (
                <Icon
                  icon="speakerOn"
                  onClick={() => setIsMuted(true)}
                  size={28}
                />
              )}
            </StyledVolumeWrapper>
          )}
        </>
      )}
      {withOverlay && (
        <VideoOverlay
          isPauseable={isPauseable}
          isPaused={isPaused}
          overlayBackground={overlayBackground}
          overlayColor={overlayColor}
          setIsPaused={setIsPaused}
          videoHeroType={videoHeroType}
        >
          {headingText && (
            <StyledContentWrapper className="d-flex justify-center align-items-center flex-column mt-5 video-over-header-text">
              {headingText}
            </StyledContentWrapper>
          )}
        </VideoOverlay>
      )}
      {children}
    </VideoWrapperComponent>
  );
}

MuxVideo.defaultProps = {
  children: null,
  loop: false,
  height: null,
  isBackgroundVideo: true,
  isPauseable: true,
  isPaused: true,
  minHeight: null,
  mobileMinHeight: null,
  muted: false,
  overlayBackground: "rgba(0, 0, 0, 0.25)",
  overlayColor: "transparent",
  setIsPaused: null,
  volumeToggle: false,
  withOverlay: true,
  playbackId: null,
  videoHeroType: "classic",
  headingText: null,
  posterUrl: null,
};

MuxVideo.propTypes = {
  children: PropTypes.element,
  loop: PropTypes.bool,
  headingText: PropTypes.element,
  height: PropTypes.string,
  isBackgroundVideo: PropTypes.bool,
  isPauseable: PropTypes.bool,
  isPaused: PropTypes.bool,
  minHeight: PropTypes.string,
  mobileMinHeight: PropTypes.string,
  muted: PropTypes.bool,
  overlayBackground: PropTypes.string,
  overlayColor: PropTypes.string,
  setIsPaused: PropTypes.func,
  videoHeroType: PropTypes.string,
  playbackId: PropTypes.string,
  volumeToggle: PropTypes.bool,
  withOverlay: PropTypes.bool,
  posterUrl: PropTypes.string,
};

export default observer(MuxVideo);
